import { isParameterValueAmendOperation } from '@festina/configuration-general-types/configuration'
import { IconButton } from '@festina-life/ui/actions'
import { Alert } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'

import { downloadConfigurationFile } from '../../../components/branchesPanel/branchDetails/helpers'
import { useParameterContext } from '../../../context/parameterContext'
import { useParameterDefinitionId } from '../../../hooks/useParameterDefinitionId'
import { useNavigationParams } from '../../../system/useNavigationParams'
import { toAmendmentsBranchChangesets } from './helpers'

/**
 * Replaces the values table for a parameter whose current view holds amendments.
 *
 * The portal deliberately shows no values for such a parameter so this states why the table is
 * absent.
 *
 * Selecting an amendment in the sidebar turns the alert into one about that amendment alone: it
 * says the amendment itself cannot be viewed here and narrows the download to it, so a reviewer
 * approving it can read exactly what they are approving.
 */
export const ParameterAmendmentsAlert = () => {
  const translate = useTranslate()
  const { reviewingOperation, amendOperationsInView } = useParameterContext()
  const { layer } = useNavigationParams()
  const parameterDefinitionId = useParameterDefinitionId()

  const selectedAmendment =
    reviewingOperation &&
    isParameterValueAmendOperation(reviewingOperation.internalOperation)
      ? reviewingOperation
      : undefined

  const handleDownload = () =>
    downloadConfigurationFile(
      toAmendmentsBranchChangesets({
        amendments: amendOperationsInView,
        layer,
      }),
      selectedAmendment
        ? `${parameterDefinitionId}-amendment.json`
        : `${parameterDefinitionId}-amendments.json`,
    )

  const downloadLabel = selectedAmendment
    ? translate('parameterViewer.values.amendments.downloadSelected')
    : translate('parameterViewer.values.amendments.download')

  const title = selectedAmendment
    ? translate('parameterViewer.values.amendments.selectedTitle')
    : translate('parameterViewer.values.amendments.title')

  const description = selectedAmendment
    ? translate('parameterViewer.values.amendments.selectedDescription')
    : translate('parameterViewer.values.amendments.description')

  return (
    <Alert
      severity="warning"
      title={title}
      className="mt-2 max-w-full!"
      action={
        <IconButton
          variant="secondary"
          className="text-inherit!"
          iconName="ArrowDownTray"
          tip={downloadLabel}
          aria-label={downloadLabel}
          onClick={handleDownload}
        />
      }
    >
      {description}
    </Alert>
  )
}
